import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../context/ThemeContext';
import { dashboardAPI } from '../lib/api';
import axios from 'axios';
import { 
  Trophy, 
  Medal,
  TrendingUp,
  TrendingDown,
  Minus,
  Star,
  Target,
  Award,
  AlertCircle,
  ThumbsUp,
  Crown,
  Info,
  MapPin,
  Briefcase,
  GitCompare,
  XCircle,
  BarChart3
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { DesignerReportDownload } from '../components/DesignerReportDownload';
import { CompareDesignersDialog } from '../components/CompareDesignersDialog';
import { BellCurveSVG } from '../components/BellCurveSVG';
import { BellCurveMiniCard } from '../components/BellCurveMiniCard';
import { cn } from '../lib/utils';
import { motion } from 'framer-motion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

const StackRanking = () => {
  const { isDark } = useTheme();
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [locationFilter, setLocationFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [subTeamFilter, setSubTeamFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [compareOpen, setCompareOpen] = useState(false);

  const MAX_COMPARE = 4;

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (next.size >= MAX_COMPARE) return prev; // cap
        next.add(id);
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const now = new Date();
  const [selectedYear, setSelectedYear] = useState(String(now.getFullYear()));
  const [selectedMonth, setSelectedMonth] = useState(String(now.getMonth() + 1));

  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const years = [];
  for (let y = 2024; y <= now.getFullYear() + 1; y++) years.push(String(y));

  // Bell Curve state
  const [bellCurveData, setBellCurveData] = useState(null);
  const [bellCurveLoading, setBellCurveLoading] = useState(false);
  const [bellCurveView, setBellCurveView] = useState('combined'); // 'combined' | 'sub_team' | 'location'
  const [groupedCurves, setGroupedCurves] = useState({ sub_team: null, location: null });
  const [groupedLoading, setGroupedLoading] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchRankings = async () => {
    setLoading(true);
    try {
      const response = await dashboardAPI.getStackRanking({
        year: parseInt(selectedYear),
        month: parseInt(selectedMonth)
      });
      setRankings(response.data);
    } catch (error) {
      console.error('Error fetching rankings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBellCurve = useCallback(async () => {
    setBellCurveLoading(true);
    try {
      const token = localStorage.getItem('arrise_token');
      const params = {
        start_year: parseInt(selectedYear),
        start_month: parseInt(selectedMonth),
        end_year: parseInt(selectedYear),
        end_month: parseInt(selectedMonth),
      };
      if (subTeamFilter && subTeamFilter !== 'all') {
        params.sub_team = subTeamFilter;
      }
      const res = await axios.get(`${BACKEND_URL}/api/reports/bell-curve`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setBellCurveData(res.data);
    } catch (err) {
      console.error('Failed to fetch bell curve:', err);
      setBellCurveData(null);
    } finally {
      setBellCurveLoading(false);
    }
  }, [BACKEND_URL, selectedYear, selectedMonth, subTeamFilter]);

  const fetchGroupedCurves = useCallback(async (groupBy) => {
    if (groupBy === 'combined') return;
    setGroupedLoading(true);
    try {
      const token = localStorage.getItem('arrise_token');
      const res = await axios.get(`${BACKEND_URL}/api/reports/bell-curve/grouped`, {
        params: {
          start_year: parseInt(selectedYear),
          start_month: parseInt(selectedMonth),
          end_year: parseInt(selectedYear),
          end_month: parseInt(selectedMonth),
          group_by: groupBy,
        },
        headers: { Authorization: `Bearer ${token}` },
      });
      setGroupedCurves((prev) => ({ ...prev, [groupBy]: res.data.groups || [] }));
    } catch (err) {
      console.error('Failed to fetch grouped curves:', err);
      setGroupedCurves((prev) => ({ ...prev, [groupBy]: [] }));
    } finally {
      setGroupedLoading(false);
    }
  }, [BACKEND_URL, selectedYear, selectedMonth]);

  useEffect(() => {
    fetchRankings();
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    fetchBellCurve();
  }, [fetchBellCurve]);

  // Re-fetch grouped curves whenever view, month, or year changes
  useEffect(() => {
    if (bellCurveView === 'sub_team' || bellCurveView === 'location') {
      fetchGroupedCurves(bellCurveView);
    }
  }, [bellCurveView, fetchGroupedCurves]);

  // Filter rankings by location, role, and sub-team
  const filteredRankings = rankings.filter(r => {
    const matchesLocation = locationFilter === 'all' || r.location === locationFilter;
    const matchesRole = roleFilter === 'all' || r.designer_role === roleFilter;
    const matchesSubTeam = subTeamFilter === 'all' || r.sub_team === subTeamFilter;
    return matchesLocation && matchesRole && matchesSubTeam;
  });

  // Re-rank after filtering
  const rankedFilteredRankings = filteredRankings.map((r, index) => ({
    ...r,
    filtered_rank: index + 1
  }));

  // Get unique values from data
  const roles = [...new Set(rankings.map(r => r.designer_role).filter(Boolean))].sort();
  const subTeams = [...new Set(rankings.map(r => r.sub_team).filter(Boolean))].sort();
  const locations = [...new Set(rankings.map(r => r.location).filter(Boolean))].sort();

  const getRankBadge = (rank) => {
    if (rank === 1) return { icon: Crown, color: 'text-yellow-500', bg: 'bg-yellow-100 dark:bg-yellow-900/30' };
    if (rank === 2) return { icon: Medal, color: 'text-slate-400', bg: 'bg-slate-100 dark:bg-slate-700' };
    if (rank === 3) return { icon: Medal, color: 'text-amber-600', bg: 'bg-amber-100 dark:bg-amber-900/30' };
    return { icon: null, color: 'text-slate-500', bg: 'bg-slate-50 dark:bg-slate-800' };
  };

  const getRankChangeIcon = (change) => {
    if (change > 0) return <TrendingUp className="w-4 h-4 text-green-500" />;
    if (change < 0) return <TrendingDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-slate-400" />;
  };

  // New Policy Score breakdown tooltip component
  const ScoreBreakdownTooltip = ({ designer, children }) => {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            {children}
          </TooltipTrigger>
          <TooltipContent className="w-80 p-3">
            <div className="space-y-2">
              <p className="font-bold text-sm border-b pb-1">Ranking Score Breakdown</p>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between">
                  <span>Performance Score (80%)</span>
                  <span className="font-semibold text-blue-600">{designer.performance_score} × 0.8 = {(designer.performance_score * 0.8).toFixed(1)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Contribution Score (20%)</span>
                  <span className="font-semibold text-green-600">{designer.contribution_score} × 0.2 = {(designer.contribution_score * 0.2).toFixed(1)}</span>
                </div>
                <div className="flex justify-between border-t pt-1 mt-1">
                  <span className="font-bold">Ranking Score</span>
                  <span className="font-bold text-[#331062]">{designer.ranking_score}</span>
                </div>
              </div>
              <div className="border-t pt-2 mt-2 text-xs space-y-1">
                <p className="text-slate-500">Performance Level: <span className={cn(
                  "font-medium",
                  designer.performance_level === 'Outstanding' && "text-emerald-600",
                  designer.performance_level === 'Strong Performer' && "text-blue-600",
                  designer.performance_level === 'Reliable Performer' && "text-sky-600",
                  designer.performance_level === 'Needs Improvement' && "text-amber-600",
                  designer.performance_level === 'Performance Concern' && "text-red-600"
                )}>{designer.performance_level}</span></p>
                <p className="text-slate-500">Weeks Scored: {designer.weeks_scored} (Eligible: {designer.eligible_weeks})</p>
                <p className="text-slate-500">Avg Active Days: {designer.avg_active_days}/week</p>
              </div>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full" />
      </div>
    );
  }

  const top3 = rankedFilteredRankings.slice(0, 3);
  const rest = rankedFilteredRankings.slice(3);

  const selectedDesigners = rankedFilteredRankings.filter((d) => selectedIds.has(d.designer_id));

  // Get unique locations (computed above)

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="stack-ranking-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className={cn(
            "text-2xl md:text-3xl font-bold font-outfit flex items-center gap-3",
            isDark ? "text-white" : "text-slate-800"
          )}>
            <Trophy className="w-8 h-8 text-yellow-500" />
            Stack Ranking
          </h1>
          <p className={cn("mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
            Performance-based ranking &middot; {MONTHS[parseInt(selectedMonth) - 1]} {selectedYear} &middot; {rankings.length} members
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setCompareOpen(true)}
            disabled={selectedIds.size < 2}
            className={cn(
              "bg-[#331062] hover:bg-[#4a1d8a] text-white",
              selectedIds.size < 2 && "opacity-50 cursor-not-allowed"
            )}
            data-testid="compare-btn"
          >
            <GitCompare className="w-4 h-4 mr-2" />
            Compare ({selectedIds.size})
          </Button>
          {selectedIds.size > 0 && (
            <Button
              variant="ghost"
              size="icon"
              onClick={clearSelection}
              className="h-10 w-10"
              data-testid="compare-clear-btn"
              aria-label="Clear selection"
            >
              <XCircle className="w-4 h-4 text-slate-500" />
            </Button>
          )}
        </div>
      </div>

      {/* Selection hint */}
      {selectedIds.size > 0 && (
        <div
          className={cn(
            "flex items-center justify-between px-4 py-2.5 rounded-lg border-l-4 border-[#331062]",
            isDark ? "bg-purple-900/20 text-slate-200" : "bg-purple-50 text-slate-700"
          )}
          data-testid="compare-selection-bar"
        >
          <p className="text-sm">
            <strong>{selectedIds.size}</strong> of {MAX_COMPARE} designers selected for comparison
            {selectedIds.size >= MAX_COMPARE && (
              <span className="ml-2 text-xs text-amber-600 dark:text-amber-400">
                (max reached)
              </span>
            )}
          </p>
          <div className="flex gap-2">
            {selectedIds.size >= 2 && (
              <Button
                size="sm"
                onClick={() => setCompareOpen(true)}
                className="bg-[#331062] hover:bg-[#4a1d8a] text-white"
                data-testid="compare-now-btn"
              >
                Compare Now
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <Card className={cn(isDark ? "bg-slate-800 border-slate-700" : "")}>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            {/* Month/Year */}
            <div>
              <label className={cn("text-xs font-medium mb-1 block", isDark ? "text-slate-400" : "text-slate-500")}>Month</label>
              <div className="flex gap-1">
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="w-20" data-testid="stack-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => <SelectItem key={i} value={String(i+1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-20" data-testid="stack-year-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Sub-Team */}
            <div>
              <label className={cn("text-xs font-medium mb-1 block", isDark ? "text-slate-400" : "text-slate-500")}>Sub-Team</label>
              <Select value={subTeamFilter} onValueChange={setSubTeamFilter}>
                <SelectTrigger className="w-44" data-testid="subteam-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sub-Teams</SelectItem>
                  {subTeams.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Location */}
            <div>
              <label className={cn("text-xs font-medium mb-1 block", isDark ? "text-slate-400" : "text-slate-500")}>Location</label>
              <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
                <Button
                  variant={locationFilter === 'all' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setLocationFilter('all')}
                  className={cn(locationFilter === 'all' ? 'bg-[#331062] text-white' : 'text-slate-600 dark:text-slate-400')}
                  data-testid="filter-all"
                >All</Button>
                {locations.map(loc => (
                  <Button
                    key={loc}
                    variant={locationFilter === loc ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setLocationFilter(loc)}
                    className={cn(locationFilter === loc 
                      ? loc === 'Noida' ? 'bg-green-600 text-white' : 'bg-orange-600 text-white'
                      : 'text-slate-600 dark:text-slate-400'
                    )}
                    data-testid={`filter-${loc.toLowerCase()}`}
                  ><MapPin className="w-3 h-3 mr-1" />{loc}</Button>
                ))}
              </div>
            </div>
          
            {/* Role */}
            <div>
              <label className={cn("text-xs font-medium mb-1 block", isDark ? "text-slate-400" : "text-slate-500")}>Role</label>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-48" data-testid="role-filter"><SelectValue placeholder="All Roles" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {roles.map(role => <SelectItem key={role} value={role}>{role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Clear + Count */}
            {(locationFilter !== 'all' || roleFilter !== 'all' || subTeamFilter !== 'all') && (
              <Button
                variant="ghost" size="sm"
                onClick={() => { setLocationFilter('all'); setRoleFilter('all'); setSubTeamFilter('all'); }}
                className="text-[#331062] hover:text-[#4a1d8a]"
              >Clear filters</Button>
            )}
          
            <div className={cn("px-4 py-2 rounded-lg text-sm ml-auto", isDark ? "bg-slate-700 text-slate-300" : "bg-white text-slate-600 shadow-sm")}>
              Showing: <strong>{rankedFilteredRankings.length}</strong> of {rankings.length}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Scoring Criteria Info - Updated for New Policy */}
      <Card className={cn("border-l-4 border-l-[#331062]", isDark ? "bg-slate-800 border-slate-700" : "")}>
        <CardContent className="p-4">
          <p className={cn("text-sm font-medium mb-2", isDark ? "text-white" : "text-slate-800")}>
            Ranking Formula (New Policy):
          </p>
          <div className="flex flex-wrap gap-4 text-xs">
            <span className={cn("flex items-center gap-1 px-2 py-1 rounded-full", isDark ? "bg-blue-900/40 text-blue-300" : "bg-blue-50 text-blue-700")}>
              <Target className="w-3 h-3" /> Performance Score (80%)
            </span>
            <span className={cn("flex items-center gap-1 px-2 py-1 rounded-full", isDark ? "bg-green-900/40 text-green-300" : "bg-green-50 text-green-700")}>
              <TrendingUp className="w-3 h-3" /> Contribution Score (20%)
            </span>
            <span className={cn("flex items-center gap-1 px-2 py-1 rounded-full", isDark ? "bg-amber-900/40 text-amber-300" : "bg-amber-50 text-amber-700")}>
              <AlertCircle className="w-3 h-3" /> Min 3 Active Days Required
            </span>
          </div>
          <p className={cn("text-xs mt-2", isDark ? "text-slate-400" : "text-slate-500")}>
            Performance Score = Weighted avg of 6 parameters (Quality 30%, Brief 20%, Efficiency 15%, Error Control 15%, Guidelines 10%, Professional Behaviour 10%)
          </p>
        </CardContent>
      </Card>

      {/* Bell Curve - Forced Distribution */}
      <Card className={cn(
        "border-0 shadow-sm",
        isDark ? "bg-slate-800/50" : "bg-white"
      )} data-testid="stack-ranking-bell-curve">
        <CardHeader className="pb-3">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <CardTitle className={cn(
                "flex items-center gap-2 text-lg font-outfit",
                isDark ? "text-white" : "text-slate-800"
              )}>
                <BarChart3 className="w-5 h-5 text-[#331062] dark:text-purple-400" />
                Forced Distribution Bell Curve
              </CardTitle>
              <p className={cn("text-xs mt-1", isDark ? "text-slate-400" : "text-slate-500")}>
                Target split: 15% Top &middot; 70% Middle &middot; 15% Bottom &middot; {MONTHS[parseInt(selectedMonth) - 1]} {selectedYear}
                {bellCurveView === 'combined' && subTeamFilter !== 'all' && <> &middot; <span className="font-medium">{subTeamFilter}</span></>}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* View By toggle */}
              <div className={cn(
                "inline-flex rounded-lg p-1",
                isDark ? "bg-slate-700" : "bg-slate-100"
              )} data-testid="bell-curve-view-toggle">
                {[
                  { id: 'combined', label: 'Combined' },
                  { id: 'sub_team', label: 'By Sub-Team' },
                  { id: 'location', label: 'By Location' },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setBellCurveView(opt.id)}
                    data-testid={`bell-curve-view-${opt.id}`}
                    className={cn(
                      "px-3 py-1.5 text-xs font-medium rounded-md transition-colors",
                      bellCurveView === opt.id
                        ? (isDark ? "bg-slate-900 text-white shadow" : "bg-white text-slate-900 shadow")
                        : (isDark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-slate-900")
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {bellCurveView === 'combined' && bellCurveData && bellCurveData.total > 0 && (
                <div className={cn(
                  "text-xs px-3 py-1.5 rounded-full font-medium",
                  isDark ? "bg-slate-700 text-slate-200" : "bg-slate-100 text-slate-700"
                )}>
                  {bellCurveData.total} ratings
                </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* COMBINED VIEW */}
          {bellCurveView === 'combined' && (
            <>
              {bellCurveLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : bellCurveData && bellCurveData.total > 0 ? (
                <>
                  <BellCurveSVG data={bellCurveData} isDark={isDark} />
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                    <div className={cn(
                      "p-3 rounded-lg border-l-4 border-red-500",
                      isDark ? "bg-red-900/20" : "bg-red-50"
                    )}>
                      <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Bottom (Rating 1 &amp; 2)</p>
                      <p className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
                        {bellCurveData.actual?.bottom?.pct || 0}%
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {bellCurveData.actual?.bottom?.count || 0} of {bellCurveData.total} &middot; Target 15%
                      </p>
                    </div>
                    <div className={cn(
                      "p-3 rounded-lg border-l-4 border-purple-500",
                      isDark ? "bg-purple-900/20" : "bg-purple-50"
                    )}>
                      <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Middle (Rating 3)</p>
                      <p className="text-2xl font-bold text-purple-700 dark:text-purple-400 mt-1">
                        {bellCurveData.actual?.middle?.pct || 0}%
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {bellCurveData.actual?.middle?.count || 0} of {bellCurveData.total} &middot; Target 70%
                      </p>
                    </div>
                    <div className={cn(
                      "p-3 rounded-lg border-l-4 border-emerald-500",
                      isDark ? "bg-emerald-900/20" : "bg-emerald-50"
                    )}>
                      <p className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400">Top (Rating 4 &amp; 5)</p>
                      <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                        {bellCurveData.actual?.top?.pct || 0}%
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                        {bellCurveData.actual?.top?.count || 0} of {bellCurveData.total} &middot; Target 15%
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <BarChart3 className="w-12 h-12 text-slate-300 mb-3" />
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                    No rating data available for {MONTHS[parseInt(selectedMonth) - 1]} {selectedYear}.
                  </p>
                </div>
              )}
            </>
          )}

          {/* GROUPED VIEW (sub_team / location) */}
          {bellCurveView !== 'combined' && (
            <>
              {groupedLoading ? (
                <div className="flex items-center justify-center py-16">
                  <div className="w-8 h-8 border-4 border-[#331062] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : groupedCurves[bellCurveView] && groupedCurves[bellCurveView].length > 0 ? (
                <div className={cn(
                  "grid gap-3",
                  bellCurveView === 'location'
                    ? "grid-cols-1 md:grid-cols-2"
                    : "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
                )} data-testid={`bell-curve-grouped-${bellCurveView}`}>
                  {groupedCurves[bellCurveView].map((group) => (
                    <BellCurveMiniCard
                      key={group.name}
                      name={group.name}
                      total={group.total}
                      actual={group.actual}
                      icon={bellCurveView === 'location' ? MapPin : Briefcase}
                      isDark={isDark}
                    />
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <BarChart3 className="w-12 h-12 text-slate-300 mb-3" />
                  <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                    No grouped data available for {MONTHS[parseInt(selectedMonth) - 1]} {selectedYear}.
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Top 3 Podium */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {top3.map((designer, index) => {
          const badge = getRankBadge(designer.filtered_rank);
          const BadgeIcon = badge.icon;
          
          return (
            <motion.div
              key={designer.designer_id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card className={cn(
                "relative overflow-hidden",
                designer.filtered_rank === 1 && "ring-2 ring-yellow-400",
                isDark ? "bg-slate-800 border-slate-700" : ""
              )}>
                {designer.filtered_rank === 1 && (
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-400" />
                )}
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className={cn(
                      "w-14 h-14 rounded-xl flex items-center justify-center text-2xl font-bold",
                      badge.bg,
                      badge.color
                    )}>
                      {BadgeIcon ? <BadgeIcon className="w-8 h-8" /> : `#${designer.filtered_rank}`}
                    </div>
                    <ScoreBreakdownTooltip designer={designer}>
                      <div className={cn(
                        "text-3xl font-bold cursor-help flex items-center gap-1",
                        isDark ? "text-white" : "text-slate-800"
                      )}>
                        {designer.ranking_score}
                        <Info className="w-4 h-4 text-slate-400" />
                      </div>
                    </ScoreBreakdownTooltip>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className={cn(
                      "font-bold text-lg",
                      isDark ? "text-white" : "text-slate-800"
                    )}>
                      {designer.designer_name}
                    </h3>
                    {designer.location && (
                      <span className={cn(
                        "px-1.5 py-0.5 rounded text-[10px] font-medium",
                        designer.location === 'Noida' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-orange-100 text-orange-700'
                      )}>
                        {designer.location}
                      </span>
                    )}
                  </div>
                  <p className={cn("text-sm mb-1", isDark ? "text-slate-400" : "text-slate-500")}>
                    {designer.employee_id} • {designer.designer_role}
                  </p>
                  <p className={cn(
                    "text-xs font-medium px-2 py-0.5 rounded-full inline-block mb-3",
                    designer.performance_level === 'Outstanding' && "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
                    designer.performance_level === 'Strong Performer' && "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
                    designer.performance_level === 'Reliable Performer' && "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300",
                    designer.performance_level === 'Needs Improvement' && "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
                    designer.performance_level === 'Performance Concern' && "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300"
                  )}>
                    {designer.performance_level}
                  </p>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className={cn(
                      "p-2 rounded-lg",
                      isDark ? "bg-slate-700" : "bg-slate-50"
                    )}>
                      <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Performance</p>
                      <p className={cn("font-bold text-blue-600 dark:text-blue-400")}>
                        {designer.performance_score}
                      </p>
                    </div>
                    <div className={cn(
                      "p-2 rounded-lg",
                      isDark ? "bg-slate-700" : "bg-slate-50"
                    )}>
                      <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Contribution</p>
                      <p className={cn("font-bold text-green-600 dark:text-green-400")}>
                        {designer.contribution_score}%
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Rest of Rankings Table */}
      <Card className={isDark ? "bg-slate-800 border-slate-700" : ""}>
        <CardHeader>
          <CardTitle className={cn("text-lg", isDark ? "text-white" : "text-slate-800")}>
            Full Rankings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={cn(
                  "text-left text-xs uppercase tracking-wider",
                  isDark ? "text-slate-400" : "text-slate-500"
                )}>
                  <th className="pb-3 pl-2 w-10"></th>
                  <th className="pb-3">Rank</th>
                  <th className="pb-3">Employee</th>
                  <th className="pb-3">Role</th>
                  <th className="pb-3 text-center">Performance</th>
                  <th className="pb-3 text-center">Contribution</th>
                  <th className="pb-3 text-center">Level</th>
                  <th className="pb-3 text-right pr-2">Ranking Score</th>
                  <th className="pb-3 text-center">Report</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {rankedFilteredRankings.map((designer, index) => {
                  const badge = getRankBadge(designer.filtered_rank);
                  const isSelected = selectedIds.has(designer.designer_id);
                  const atCap = selectedIds.size >= MAX_COMPARE && !isSelected;
                  
                  return (
                    <motion.tr
                      key={designer.designer_id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: index * 0.02 }}
                      className={cn(
                        "hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors",
                        designer.filtered_rank <= 3 && "bg-yellow-50/50 dark:bg-yellow-900/10",
                        !designer.ranking_eligible && "opacity-60",
                        isSelected && "bg-purple-50 dark:bg-purple-900/20"
                      )}
                    >
                      <td className="py-3 pl-2">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelect(designer.designer_id)}
                          disabled={atCap}
                          data-testid={`compare-check-${designer.designer_id}`}
                          aria-label={`Select ${designer.designer_name} for comparison`}
                        />
                      </td>
                      <td className="py-3">
                        {designer.ranking_eligible ? (
                          <div className={cn(
                            "w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm",
                            badge.bg,
                            badge.color
                          )}>
                            {designer.filtered_rank}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">N/A</span>
                        )}
                      </td>
                      <td className="py-3">
                        <div className="flex items-center gap-2">
                          <div>
                            <p className={cn("font-medium", isDark ? "text-white" : "text-slate-800")}>
                              {designer.designer_name}
                            </p>
                            <p className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>
                              {designer.employee_id}
                            </p>
                          </div>
                          {designer.location && (
                            <span className={cn(
                              "px-1.5 py-0.5 rounded text-[10px] font-medium",
                              designer.location === 'Noida' 
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                                : 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
                            )}>
                              {designer.location}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className={cn("py-3 text-sm", isDark ? "text-slate-300" : "text-slate-600")}>
                        {designer.designer_role}
                      </td>
                      <td className="py-3 text-center">
                        <span className="font-bold text-blue-600 dark:text-blue-400">
                          {designer.performance_score}
                        </span>
                      </td>
                      <td className="py-3 text-center">
                        <span className="font-bold text-green-600 dark:text-green-400">
                          {designer.contribution_score}%
                        </span>
                      </td>
                      <td className="py-3 text-center">
                        <span className={cn(
                          "inline-flex items-center px-2 py-1 rounded-full text-xs font-medium",
                          designer.performance_level === 'Outstanding' && "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
                          designer.performance_level === 'Strong Performer' && "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
                          designer.performance_level === 'Reliable Performer' && "bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400",
                          designer.performance_level === 'Needs Improvement' && "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
                          designer.performance_level === 'Performance Concern' && "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                        )}>
                          {designer.performance_level || designer.ranking_status}
                        </span>
                      </td>
                      <td className="py-3 text-right pr-2">
                        <ScoreBreakdownTooltip designer={designer}>
                          <span className={cn(
                            "text-lg font-bold cursor-help inline-flex items-center gap-1",
                            isDark ? "text-white" : "text-slate-800"
                          )}>
                            {designer.ranking_score}
                            <Info className="w-3 h-3 text-slate-400" />
                          </span>
                        </ScoreBreakdownTooltip>
                      </td>
                      <td className="py-3 text-center">
                        <DesignerReportDownload
                          designerId={designer.designer_id}
                          designerName={designer.designer_name}
                          testIdPrefix="stack-report"
                        />
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <CompareDesignersDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        designers={selectedDesigners}
      />
    </div>
  );
};

export default StackRanking;
